/**
 * Mission Store (Zustand)
 *
 * Centralized state management for mission planning
 * Handles missions, waypoints, selection, and undo/redo functionality
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

import type { Mission, MissionStatus, Position, VehicleType } from '@/features/mission/types/mission.types';
import type { Waypoint, WaypointInput, WaypointUpdate } from '@/features/mission/types/waypoint.types';
import {
  FirmwareType,
  MissionSettings,
  ValidationResult,
} from '@/features/mission/types/settings.types';
import { MISSION_DEFAULTS, WAYPOINT_DEFAULTS, AUTOSAVE_SETTINGS } from '@/constants/mission-defaults';
import { MavCommand } from '@/constants/mavlink-commands';
import { WaypointFrame } from '@/features/mission/types/waypoint.types';
import { MissionStatus as MissionStatusEnum } from '@/features/mission/types/mission.types';

/**
 * Mission Store State Interface
 */
interface MissionStore {
  // Current Mission State
  currentMission: Mission | null;
  missions: Mission[];

  // Home Position
  homePosition: Position | null;

  // Selected Items
  selectedWaypointId: string | null;
  hoveredWaypointId: string | null;

  // UI State
  isAddingWaypoint: boolean;
  isEditingWaypoint: boolean;

  // History for Undo/Redo
  history: Mission[];
  historyIndex: number;

  // Actions - Mission CRUD
  createMission: (name: string, vehicleType?: VehicleType) => void;
  loadMission: (missionId: string) => void;
  saveMission: () => void;
  deleteMission: (missionId: string) => void;
  updateMissionStatus: (status: MissionStatus) => void;
  updateMissionName: (name: string) => void;
  updateMissionDescription: (description: string) => void;
  updateMissionSettings: (settings: Partial<MissionSettings>) => void;
  duplicateMission: (missionId: string) => void;

  // Actions - Waypoint Operations
  addWaypoint: (input: WaypointInput) => void;
  updateWaypoint: (waypointId: string, updates: WaypointUpdate) => void;
  deleteWaypoint: (waypointId: string) => void;
  reorderWaypoints: (startIndex: number, endIndex: number) => void;
  insertWaypoint: (afterIndex: number, input: WaypointInput) => void;
  clearAllWaypoints: () => void;

  // Actions - Selection
  selectWaypoint: (waypointId: string | null) => void;
  setHoveredWaypoint: (waypointId: string | null) => void;

  // Actions - Home Position
  setHomePosition: (position: Position) => void;

  // Actions - UI Mode
  setAddingWaypointMode: (isAdding: boolean) => void;
  setEditingWaypointMode: (isEditing: boolean) => void;

  // Actions - History
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  saveToHistory: () => void;

  // Actions - Validation
  validateMission: () => ValidationResult[];
  clearValidation: () => void;

  // Actions - Utility
  getWaypointById: (waypointId: string) => Waypoint | undefined;
  getNextSequence: () => number;
}

/**
 * Create a new empty mission
 */
function createEmptyMission(name: string, vehicleType: VehicleType = MISSION_DEFAULTS.defaultVehicleType): Mission {
  return {
    id: uuidv4(),
    name,
    description: '',
    waypoints: [],
    homePosition: undefined,
    defaultAltitude: MISSION_DEFAULTS.defaultAltitude,
    defaultSpeed: MISSION_DEFAULTS.defaultSpeed,
    cruiseSpeed: MISSION_DEFAULTS.cruiseSpeed,
    maxSpeed: MISSION_DEFAULTS.maxSpeed,
    vehicleType,
    status: MissionStatusEnum.PLANNING,
    totalDistance: 0,
    estimatedTime: 0,
    estimatedBattery: 0,
    maxAltitude: 0,
    minAltitude: 0,
    altitudeChange: 0,
    createdAt: new Date(),
    updatedAt: new Date(),
    version: 1,
  };
}

/**
 * Create waypoint from input
 */
function createWaypointFromInput(input: WaypointInput, sequence: number): Waypoint {
  return {
    id: uuidv4(),
    sequence,
    latitude: input.latitude,
    longitude: input.longitude,
    altitude: input.altitude,
    command: input.command ?? MavCommand.NAV_WAYPOINT,
    frame: input.frame ?? WaypointFrame.GLOBAL_RELATIVE_ALT_INT,
    param1: input.param1 ?? WAYPOINT_DEFAULTS.param1,
    param2: input.param2 ?? WAYPOINT_DEFAULTS.param2,
    param3: input.param3 ?? WAYPOINT_DEFAULTS.param3,
    param4: input.param4 ?? WAYPOINT_DEFAULTS.param4,
    isCurrentWaypoint: false,
    autocontinue: input.autocontinue ?? WAYPOINT_DEFAULTS.autocontinue,
    isSelected: false,
    isHovered: false,
    // Optional user metadata
    label: input.label,
    notes: input.notes,
    color: input.color,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Resequence waypoints to ensure correct ordering
 */
function resequenceWaypoints(waypoints: Waypoint[]): Waypoint[] {
  return waypoints.map((wp, index) => ({
    ...wp,
    sequence: index,
  }));
}

/**
 * Mission Store Implementation
 */
export const useMissionStore = create<MissionStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State
        currentMission: null,
        missions: [],
        homePosition: null,
        selectedWaypointId: null,
        hoveredWaypointId: null,
        isAddingWaypoint: false,
        isEditingWaypoint: false,
        history: [],
        historyIndex: -1,

        // Mission CRUD Operations
        createMission: (name, vehicleType) => {
          const newMission = createEmptyMission(name, vehicleType);
          set((state) => ({
            currentMission: newMission,
            missions: [...state.missions, newMission],
            history: [newMission],
            historyIndex: 0,
          }));
        },

        loadMission: (missionId) => {
          const mission = get().missions.find((m) => m.id === missionId);
          if (mission) {
            set({
              currentMission: mission,
              history: [mission],
              historyIndex: 0,
            });
          }
        },

        saveMission: () => {
          const { currentMission, missions } = get();
          if (!currentMission) return;

          const updatedMission = {
            ...currentMission,
            updatedAt: new Date(),
            version: currentMission.version + 1,
          };

          set({
            currentMission: updatedMission,
            missions: missions.map((m) =>
              m.id === updatedMission.id ? updatedMission : m
            ),
          });
        },

        deleteMission: (missionId) => {
          set((state) => ({
            missions: state.missions.filter((m) => m.id !== missionId),
            currentMission: state.currentMission?.id === missionId ? null : state.currentMission,
          }));
        },

        updateMissionStatus: (status) => {
          const { currentMission } = get();
          if (!currentMission) return;

          set({
            currentMission: {
              ...currentMission,
              status,
              updatedAt: new Date(),
            },
          });
        },

        updateMissionName: (name) => {
          const { currentMission } = get();
          if (!currentMission) return;

          set({
            currentMission: {
              ...currentMission,
              name,
              updatedAt: new Date(),
            },
          });
        },

        updateMissionDescription: (description) => {
          const { currentMission } = get();
          if (!currentMission) return;

          set({
            currentMission: {
              ...currentMission,
              description,
              updatedAt: new Date(),
            },
          });
        },

        updateMissionSettings: (settings) => {
          const { currentMission } = get();
          if (!currentMission) return;

          // Merge settings with existing values
          const updatedMission = {
            ...currentMission,
            updatedAt: new Date(),
          };

          // Update individual fields if provided
          if (settings.defaultAltitude !== undefined) {
            updatedMission.defaultAltitude = settings.defaultAltitude;
          }
          if (settings.defaultSpeed !== undefined) {
            updatedMission.defaultSpeed = settings.defaultSpeed;
          }
          if (settings.cruiseSpeed !== undefined) {
            updatedMission.cruiseSpeed = settings.cruiseSpeed;
          }
          if (settings.maxSpeed !== undefined) {
            updatedMission.maxSpeed = settings.maxSpeed;
          }
          if (settings.vehicleType !== undefined) {
            updatedMission.vehicleType = settings.vehicleType;
          }

          // Store complete settings object
          updatedMission.settings = {
            defaultAltitude: updatedMission.defaultAltitude,
            defaultSpeed: updatedMission.defaultSpeed,
            cruiseSpeed: updatedMission.cruiseSpeed,
            maxSpeed: updatedMission.maxSpeed,
            vehicleType: updatedMission.vehicleType,
            firmwareType: settings.firmwareType ?? updatedMission.settings?.firmwareType ?? FirmwareType.ARDUPILOT,
            safetySettings: settings.safetySettings ?? updatedMission.settings?.safetySettings,
          };

          set({ currentMission: updatedMission });
          get().saveToHistory();
        },

        duplicateMission: (missionId) => {
          const { missions } = get();
          const mission = missions.find((m) => m.id === missionId);
          if (!mission) return;

          const duplicatedMission: Mission = {
            ...mission,
            id: uuidv4(),
            name: `${mission.name} (Copy)`,
            status: MissionStatusEnum.PLANNING,
            createdAt: new Date(),
            updatedAt: new Date(),
            version: 1,
            lastUploadedAt: undefined,
            // Clear validation state
            validation: undefined,
            // Deep copy waypoints with new IDs
            waypoints: mission.waypoints.map((wp) => ({
              ...wp,
              id: uuidv4(),
              isSelected: false,
              isHovered: false,
              isCurrentWaypoint: false,
              createdAt: new Date(),
              updatedAt: new Date(),
            })),
          };

          set((state) => ({
            missions: [...state.missions, duplicatedMission],
            currentMission: duplicatedMission,
            history: [duplicatedMission],
            historyIndex: 0,
          }));
        },

        // Waypoint Operations
        addWaypoint: (input) => {
          const { currentMission } = get();
          if (!currentMission) return;

          const sequence = currentMission.waypoints.length;
          const newWaypoint = createWaypointFromInput(input, sequence);

          const updatedMission = {
            ...currentMission,
            waypoints: [...currentMission.waypoints, newWaypoint],
            updatedAt: new Date(),
          };

          set({ currentMission: updatedMission });
          get().saveToHistory();
        },

        updateWaypoint: (waypointId, updates) => {
          const { currentMission } = get();
          if (!currentMission) return;

          const updatedWaypoints = currentMission.waypoints.map((wp) =>
            wp.id === waypointId
              ? { ...wp, ...updates, updatedAt: new Date() }
              : wp
          );

          set({
            currentMission: {
              ...currentMission,
              waypoints: updatedWaypoints,
              updatedAt: new Date(),
            },
          });
          get().saveToHistory();
        },

        deleteWaypoint: (waypointId) => {
          const { currentMission } = get();
          if (!currentMission) return;

          const filteredWaypoints = currentMission.waypoints.filter(
            (wp) => wp.id !== waypointId
          );

          const resequenced = resequenceWaypoints(filteredWaypoints);

          set({
            currentMission: {
              ...currentMission,
              waypoints: resequenced,
              updatedAt: new Date(),
            },
            selectedWaypointId: null,
          });
          get().saveToHistory();
        },

        reorderWaypoints: (startIndex, endIndex) => {
          const { currentMission } = get();
          if (!currentMission) return;

          const waypoints = [...currentMission.waypoints];
          const [removed] = waypoints.splice(startIndex, 1);
          waypoints.splice(endIndex, 0, removed);

          const resequenced = resequenceWaypoints(waypoints);

          set({
            currentMission: {
              ...currentMission,
              waypoints: resequenced,
              updatedAt: new Date(),
            },
          });
          get().saveToHistory();
        },

        insertWaypoint: (afterIndex, input) => {
          const { currentMission } = get();
          if (!currentMission) return;

          const newWaypoint = createWaypointFromInput(input, afterIndex + 1);
          const waypoints = [...currentMission.waypoints];
          waypoints.splice(afterIndex + 1, 0, newWaypoint);

          const resequenced = resequenceWaypoints(waypoints);

          set({
            currentMission: {
              ...currentMission,
              waypoints: resequenced,
              updatedAt: new Date(),
            },
          });
          get().saveToHistory();
        },

        clearAllWaypoints: () => {
          const { currentMission } = get();
          if (!currentMission) return;

          set({
            currentMission: {
              ...currentMission,
              waypoints: [],
              updatedAt: new Date(),
            },
            selectedWaypointId: null,
          });
          get().saveToHistory();
        },

        // Selection
        selectWaypoint: (waypointId) => {
          const { currentMission } = get();
          if (!currentMission) return;

          const updatedWaypoints = currentMission.waypoints.map((wp) => ({
            ...wp,
            isSelected: wp.id === waypointId,
          }));

          set({
            currentMission: {
              ...currentMission,
              waypoints: updatedWaypoints,
            },
            selectedWaypointId: waypointId,
          });
        },

        setHoveredWaypoint: (waypointId) => {
          const { currentMission } = get();
          if (!currentMission) return;

          const updatedWaypoints = currentMission.waypoints.map((wp) => ({
            ...wp,
            isHovered: wp.id === waypointId,
          }));

          set({
            currentMission: {
              ...currentMission,
              waypoints: updatedWaypoints,
            },
            hoveredWaypointId: waypointId,
          });
        },

        // Home Position
        setHomePosition: (position) => {
          set({ homePosition: position });
        },

        // UI Mode
        setAddingWaypointMode: (isAdding) => {
          set({ isAddingWaypoint: isAdding });
        },

        setEditingWaypointMode: (isEditing) => {
          set({ isEditingWaypoint: isEditing });
        },

        // History (Undo/Redo)
        saveToHistory: () => {
          const { currentMission, history, historyIndex } = get();
          if (!currentMission) return;

          // Remove any future history if we're not at the end
          const newHistory = history.slice(0, historyIndex + 1);

          // Add current state to history
          newHistory.push({ ...currentMission });

          // Limit history size
          const limitedHistory = newHistory.slice(-AUTOSAVE_SETTINGS.maxHistoryStates);

          set({
            history: limitedHistory,
            historyIndex: limitedHistory.length - 1,
          });
        },

        undo: () => {
          const { history, historyIndex } = get();
          if (!get().canUndo()) return;

          const newIndex = historyIndex - 1;
          const previousMission = history[newIndex];

          set({
            currentMission: { ...previousMission },
            historyIndex: newIndex,
          });
        },

        redo: () => {
          const { history, historyIndex } = get();
          if (!get().canRedo()) return;

          const newIndex = historyIndex + 1;
          const nextMission = history[newIndex];

          set({
            currentMission: { ...nextMission },
            historyIndex: newIndex,
          });
        },

        canUndo: () => {
          const { historyIndex } = get();
          return historyIndex > 0;
        },

        canRedo: () => {
          const { history, historyIndex } = get();
          return historyIndex < history.length - 1;
        },

        // Validation
        validateMission: () => {
          const { currentMission } = get();
          if (!currentMission) return [];

          const results: ValidationResult[] = [];

          // Basic validation - will be expanded with mission-validator.ts
          // For now, just return empty results
          // This will be properly implemented when we create the validator utility

          // Store validation results in mission
          const validatedMission = {
            ...currentMission,
            validation: {
              isValid: results.filter((r) => r.level === 'error').length === 0,
              canUpload: results.filter((r) => r.level === 'error').length === 0,
              results,
              lastValidated: new Date(),
            },
          };

          set({ currentMission: validatedMission });
          return results;
        },

        clearValidation: () => {
          const { currentMission } = get();
          if (!currentMission) return;

          set({
            currentMission: {
              ...currentMission,
              validation: undefined,
            },
          });
        },

        // Utility
        getWaypointById: (waypointId) => {
          const { currentMission } = get();
          return currentMission?.waypoints.find((wp) => wp.id === waypointId);
        },

        getNextSequence: () => {
          const { currentMission } = get();
          return currentMission?.waypoints.length ?? 0;
        },
      }),
      {
        name: 'mission-storage',
        partialize: (state) => ({
          missions: state.missions,
          currentMission: state.currentMission,
          homePosition: state.homePosition,
        }),
      }
    ),
    {
      name: 'MissionStore',
    }
  )
);
